import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo, useRef } from "react";
import { useMarkReadConversationAction } from "../actions/use-conversation-actions";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

/**
 * Hook that automatically marks a conversation as read when it's first viewed.
 * Uses useMemo to ensure the side effect only runs once per conversation.
 * Tracks the last processed conversation ID to handle navigation between conversations.
 */
export function useMarkConversationAsRead(
  conversationId: ConversationId,
): void {
  const markReadAction = useMarkReadConversationAction();
  const lastConversationIdRef = useRef<ConversationId | null>(null);
  const collections = useGlobalConversationCollections();
  const conversation = useLiveQuery(
    (q) =>
      q
        .from({ c: collections.conversationsList })
        .where(({ c }) => eq(c.id, conversationId))
        .findOne(),
    [conversationId],
  ).data;

  useMemo(() => {
    if (
      conversation &&
      conversation.readAt === null &&
      lastConversationIdRef.current !== conversationId
    ) {
      void markReadAction.execute(conversationId);
      lastConversationIdRef.current = conversationId;
    }
  }, [conversation, conversationId, markReadAction]);
}
